import * as React from 'react';

import { Button, cn } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { Check, FileCheck, PackageCheck, Ship } from 'lucide-react';

import * as state from '../../_state/bookingDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { BookingLoadStatusSummary } from '../../types/booking';

interface VoyageStepProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  total: number;
  isComplete: boolean;
  isActive: boolean;
}

const VoyageStep = ({
  icon,
  label,
  count,
  total,
  isComplete,
  isActive,
}: VoyageStepProps) => (
  <div className="@VoyageStep flex flex-col items-center gap-1">
    <div
      className={cn(
        'flex size-10 items-center justify-center rounded-full border-2',
        isComplete
          ? 'border-success bg-success text-white'
          : isActive
            ? 'border-primary bg-primary/10 text-primary'
            : 'border-neutral/30 bg-neutral/10 text-neutral/50',
      )}
    >
      {isComplete ? <Check className="size-5" /> : icon}
    </div>
    <span
      className={cn(
        'text-sm font-medium',
        isComplete
          ? 'text-success'
          : isActive
            ? 'text-primary'
            : 'text-neutral/50',
      )}
    >
      {label}
    </span>
    <span className="text-xs text-neutral/60">
      {count}/{total}
    </span>
  </div>
);

interface VoyageConnectorProps {
  isComplete: boolean;
}

const VoyageConnector = ({ isComplete }: VoyageConnectorProps) => (
  <div
    className={cn(
      '@VoyageConnector',
      cn('h-0.5 flex-1', isComplete ? 'bg-success' : 'bg-neutral/20'),
    )}
  />
);

interface BookingVoyageTrackerProps {
  bookingId: string;
  loadStatusSummary?: BookingLoadStatusSummary;
}

export const BookingVoyageTracker = ({
  bookingId,
  loadStatusSummary,
}: BookingVoyageTrackerProps) => {
  const t = useTranslations();
  const toast = useToast();
  const [isReceiving, setIsReceiving] = React.useState(false);
  const [isReconciling, setIsReconciling] = React.useState(false);

  const receiveAllLoads = useSetAtom(state.$receiveAllLoads);
  const reconcileAllLoads = useSetAtom(state.$reconcileAllLoads);
  const { refetch } = useAtomValue(state.$remoteBookingDetail);

  if (!loadStatusSummary || loadStatusSummary.total === 0) {
    return null;
  }

  const {
    total,
    shipped,
    received,
    reconciled,
    allShipped,
    allReceived,
    allReconciled,
  } = loadStatusSummary;

  const handleReceiveAll = async () => {
    if (shipped === 0) return;

    setIsReceiving(true);
    try {
      await receiveAllLoads({ bookingId });
      toast.success(t.bookingDetail.voyageTracker.receiveSuccess(shipped));
      refetch?.();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t.bookingDetail.voyageTracker.receiveFailed,
      );
    } finally {
      setIsReceiving(false);
    }
  };

  const handleReconcileAll = async () => {
    if (received === 0) return;

    setIsReconciling(true);
    try {
      await reconcileAllLoads({ bookingId });
      toast.success(t.bookingDetail.voyageTracker.reconcileSuccess(received));
      refetch?.();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t.bookingDetail.voyageTracker.reconcileFailed,
      );
    } finally {
      setIsReconciling(false);
    }
  };

  const shippedCount = shipped + received + reconciled;
  const receivedCount = received + reconciled;
  const reconciledCount = reconciled;

  return (
    <div className={cn('@BookingVoyageTracker', 'p-5 pb-0')}>
      <div className="rounded-lg border border-neutral/20 bg-white px-6 py-4">
        <h3 className="mb-4 text-sm font-semibold text-neutral/80">
          {t.bookingDetail.voyageTracker.title}
        </h3>

        <div className="mb-6 flex items-center gap-2">
          <VoyageStep
            icon={<Ship className="size-5" />}
            label={t.bookingDetail.voyageTracker.shipped}
            count={shippedCount}
            total={total}
            isComplete={allShipped}
            isActive={!allShipped && shippedCount > 0}
          />
          <VoyageConnector isComplete={allShipped} />
          <VoyageStep
            icon={<PackageCheck className="size-5" />}
            label={t.bookingDetail.voyageTracker.received}
            count={receivedCount}
            total={total}
            isComplete={allReceived}
            isActive={allShipped && !allReceived && receivedCount > 0}
          />
          <VoyageConnector isComplete={allReceived} />
          <VoyageStep
            icon={<FileCheck className="size-5" />}
            label={t.bookingDetail.voyageTracker.reconciled}
            count={reconciledCount}
            total={total}
            isComplete={allReconciled}
            isActive={allReceived && !allReconciled && reconciledCount > 0}
          />
        </div>

        <div className="flex gap-2">
          {shipped > 0 && !allReceived && (
            <Button
              variant="outline"
              color="primary"
              size="small"
              onPress={handleReceiveAll}
              isDisabled={isReceiving}
            >
              {isReceiving
                ? t.bookingDetail.voyageTracker.receiving
                : t.bookingDetail.voyageTracker.receiveAll(shipped)}
            </Button>
          )}
          {received > 0 && !allReconciled && (
            <Button
              variant="outline"
              color="primary"
              size="small"
              onPress={handleReconcileAll}
              isDisabled={isReconciling}
            >
              {isReconciling
                ? t.bookingDetail.voyageTracker.reconciling
                : t.bookingDetail.voyageTracker.reconcileAll(received)}
            </Button>
          )}
          {allReconciled && (
            <span className="text-sm text-success">
              {t.bookingDetail.voyageTracker.allComplete}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
